// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import type { WellFormedError } from '@/api/types';
import {
  UpdateProjectLineItemParameters,
  updateProjectLineItem,
} from '@/project/api/update-project-line-item';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface UseUpdateProjectLineItemOptions {
  onSuccess?: () => void;
  onError?: (error: AxiosError, apiError: WellFormedError) => void;
  // Extra query keys to invalidate alongside the project + charges
  // refetch. Used by callers that render data downstream of the line
  // item update (e.g. the billing plans listing) so their views stay
  // in sync with the saved plan without each call site repeating the
  // invalidation wiring.
  additionalInvalidations?: string[][];
}

// Domain validation codes the project line-item update endpoint emits.
// Callers surface these inline, so they should not be reported to Sentry.
const HANDLED_API_STATUS_CODES = new Set<number>([
  ERROR_CODE_MAPPING.billing_plan_activation_date_out_of_range,
  ERROR_CODE_MAPPING.project_mixed_currency,
]);

/**
 * Mutation hook for sparse-updating a single project line item.
 * Invalidates the project query on success so the UI refreshes.
 */
export function useUpdateProjectLineItem(
  projectKey: string,
  options?: UseUpdateProjectLineItemOptions
) {
  const queryClient = useQueryClient();
  const additionalInvalidations = options?.additionalInvalidations;

  return useMutation({
    mutationFn: (parameters: UpdateProjectLineItemParameters) => {
      return updateProjectLineItem(parameters);
    },
    onSuccess: async (_data, variables) => {
      // Await the refetch before the caller's onSuccess runs (which closes
      // the drawer). Without this await, closing then immediately reopening
      // shows stale data because the parent's cache hasn't refreshed yet.
      try {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['project', projectKey],
          }),
          queryClient.invalidateQueries({
            queryKey: [
              'project',
              projectKey,
              'lineItem',
              variables.lineItemKey,
              'charges',
            ],
          }),
          ...(additionalInvalidations?.map((queryKey) =>
            queryClient.invalidateQueries({ queryKey })
          ) ?? []),
        ]);
      } catch (error) {
        captureException(error);
      }
      options?.onSuccess?.();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const isHandled = HANDLED_API_STATUS_CODES.has(apiError.apiStatusCode);
      if (!isHandled && !apiError.sentToSentry) {
        captureException(error);
      }
      options?.onError?.(error, apiError);
    },
  });
}
